import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Users, Banknote, Briefcase, GraduationCap, CalendarClock, 
  TrendingUp, Clock, FileCheck, Sparkles, ArrowRight, ArrowUpRight
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, deptRes, payrollRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/department-stats`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/payroll-summary`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setDepartmentStats(deptRes.data);
      setPayrollSummary(payrollRes.data);
    } catch (error) {
      toast.error("Error al cargar datos del dashboard");
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { 
      title: "Total Empleados", 
      value: stats?.total_employees || 0, 
      icon: Users, 
      color: "bg-emerald-500",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-600",
      link: "/employees"
    },
    { 
      title: "Vacantes Abiertas", 
      value: stats?.open_vacancies || 0, 
      icon: Briefcase, 
      color: "bg-violet-500",
      bgColor: "bg-violet-50",
      textColor: "text-violet-600",
      link: "/recruitment"
    },
    { 
      title: "Nóminas Pendientes", 
      value: stats?.pending_payroll || 0, 
      icon: Banknote, 
      color: "bg-blue-500",
      bgColor: "bg-blue-50",
      textColor: "text-blue-600",
      link: "/payroll"
    },
    { 
      title: "Capacitaciones Activas", 
      value: stats?.active_trainings || 0, 
      icon: GraduationCap, 
      color: "bg-orange-500",
      bgColor: "bg-orange-50",
      textColor: "text-orange-600",
      link: "/training"
    },
    { 
      title: "Asistencia Hoy", 
      value: stats?.today_attendance || 0, 
      icon: CalendarClock, 
      color: "bg-rose-500",
      bgColor: "bg-rose-50",
      textColor: "text-rose-600",
      link: "/attendance"
    },
    { 
      title: "Permisos Pendientes", 
      value: stats?.pending_leave_requests || 0, 
      icon: FileCheck, 
      color: "bg-amber-500",
      bgColor: "bg-amber-50",
      textColor: "text-amber-600",
      link: "/attendance"
    },
  ];

  const COLORS = ['#059669', '#3b82f6', '#7c3aed', '#f97316', '#f43f5e', '#eab308'];

  const quickActions = [
    { label: "Agregar Empleado", path: "/employees/new", icon: Users, color: "text-emerald-600" },
    { label: "Nueva Vacante", path: "/recruitment", icon: Briefcase, color: "text-violet-600" },
    { label: "Procesar Nómina", path: "/payroll", icon: Banknote, color: "text-blue-600" },
    { label: "Asistencia IA", path: "/ai-agent/engagement", icon: Sparkles, color: "text-violet-500" },
  ];

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout 
      title="Dashboard" 
      subtitle="Resumen general de tu organización"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Link 
            key={index} 
            to={stat.link}
            className="group"
            data-testid={`stat-card-${index}`}
          >
            <Card className="h-full card-hover border-slate-200">
              <CardContent className="p-4">
                <div className={`w-10 h-10 rounded-lg ${stat.bgColor} flex items-center justify-center mb-3`}>
                  <stat.icon className={`w-5 h-5 ${stat.textColor}`} />
                </div>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.title}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Department Distribution */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Empleados por Departamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {departmentStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentStats} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="department" tick={{ fontSize: 12 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="count" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Agrega empleados para ver estadísticas</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Acciones Rápidas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action, index) => (
              <Link key={index} to={action.path}>
                <Button 
                  variant="outline" 
                  className="w-full justify-between h-12 hover:bg-slate-50"
                  data-testid={`quick-action-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <action.icon className={`w-5 h-5 ${action.color}`} />
                    <span>{action.label}</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </Button>
              </Link>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Payroll Summary & AI Agents */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Payroll Summary */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Manrope' }}>
              Resumen de Nómina
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payrollSummary.length > 0 ? (
              <div className="space-y-4">
                {payrollSummary.slice(0, 4).map((period, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{period.period}</p>
                      <p className="text-sm text-slate-500">{period.employees} empleados</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-slate-900">
                        ${period.total_net?.toLocaleString('es-MX')} MXN
                      </p>
                      <p className="text-xs text-slate-500">Neto</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <Banknote className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Sin registros de nómina</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Agents */}
        <Card className="border-slate-200 bg-gradient-to-br from-violet-50 to-white">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Sparkles className="w-5 h-5 text-violet-500" />
              Agentes de IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">
              Utiliza nuestros agentes de IA especializados para optimizar tus procesos de RH.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Link to="/ai-agent/recruitment">
                <div className="p-4 rounded-lg bg-white border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer">
                  <Sparkles className="w-6 h-6 text-violet-500 mb-2" />
                  <p className="font-medium text-slate-900">Reclutamiento</p>
                  <p className="text-xs text-slate-500">Análisis de CVs</p>
                </div>
              </Link>
              <Link to="/ai-agent/payroll">
                <div className="p-4 rounded-lg bg-white border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer">
                  <Sparkles className="w-6 h-6 text-violet-500 mb-2" />
                  <p className="font-medium text-slate-900">Nómina</p>
                  <p className="text-xs text-slate-500">Validación fiscal</p>
                </div>
              </Link>
              <Link to="/ai-agent/compliance">
                <div className="p-4 rounded-lg bg-white border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer">
                  <Sparkles className="w-6 h-6 text-violet-500 mb-2" />
                  <p className="font-medium text-slate-900">Compliance</p>
                  <p className="text-xs text-slate-500">Leyes laborales</p>
                </div>
              </Link>
              <Link to="/ai-agent/engagement">
                <div className="p-4 rounded-lg bg-white border border-violet-100 hover:border-violet-300 transition-colors cursor-pointer">
                  <Sparkles className="w-6 h-6 text-violet-500 mb-2" />
                  <p className="font-medium text-slate-900">Engagement</p>
                  <p className="text-xs text-slate-500">Clima laboral</p>
                </div>
              </Link>
            </div>
            <Link to="/ai-settings" className="block mt-4">
              <Button variant="outline" className="w-full" data-testid="configure-ai-btn">
                Configurar API Key
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Dashboard;
